import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useTransitionStatus,
} from "@floating-ui/react"
import {
  useMemo,
  useState,
  useLayoutEffect,
} from "react"

import type { PopoverOptions } from "./PopoverOptions"

export function usePopover({
  initialOpen = false,
  placement = "bottom",
  modal = true,
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  transitionMs = 500,
}: PopoverOptions = {}) {
  const [
    uncontrolledOpen,
    setUncontrolledOpen,
  ] = useState(initialOpen)

  const [
    labelId,
    setLabelId,
  ] = useState<string | undefined>()

  const [
    descriptionId,
    setDescriptionId,
  ] = useState<string | undefined>()

  const [
    offsetInPixels,
    setOffsetInPixels,
  ] = useState<number>(8)

  const isOpen = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const data = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      // note that this middleware currently matches the tooltip middleware
      offset(offsetInPixels),
      flip({
        fallbackAxisSideDirection: "end",
        padding: 8,
      }),
      shift({ padding: 8 }),
    ],
  })

  const context = data.context

  const transitionData = useTransitionStatus(
    context,
    { duration: transitionMs },
  )

  const clickInteraction = useClick(context)

  const dismissInteraction = useDismiss(
    context,
    // eager dismiss on mouse input, lazy on touch input
    { outsidePressEvent: "mousedown" },
  )

  const roleInteraction = useRole(context)

  const interactions = useInteractions([
    clickInteraction,
    dismissInteraction,
    roleInteraction,
  ])

  useLayoutEffect(
    () => {
      const trigger = context.refs.reference.current

      if (isOpen && hideTrigger && trigger) {
        const bounds = trigger.getBoundingClientRect()
        const popoverSide = placement.split("-")[0]
        if (popoverSide === "left" || popoverSide === "right") {
          setOffsetInPixels(-bounds.width)
        } else {
          setOffsetInPixels(-bounds.height)
        }
      }
    },
    [
      isOpen,
      hideTrigger,
      context.refs.reference,
    ],
  )

  return useMemo(
    () => ({
      ...data,
      ...interactions,
      ...transitionData,
      descriptionId,
      hideTrigger,
      labelId,
      modal,
      open: isOpen,
      setDescriptionId,
      setLabelId,
      setOpen,
      transitionMs,
    }),
    [
      data,
      descriptionId,
      hideTrigger,
      interactions,
      isOpen,
      labelId,
      modal,
      setDescriptionId,
      setLabelId,
      setOpen,
      transitionData,
      transitionMs,
    ],
  )
}
